import { useEffect, useState } from "react";
import { toast } from "@/lib/toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

// Lightweight thumbs up/down on Fig's verdict. Writes to verdict_feedback so
// we can build a labeled dataset of where the model was right/wrong over time.
// One row per (scan, user) — re-clicking flips the value.
export function VerdictFeedback({
  scanId,
  onDisagreeNote,
}: {
  scanId: string | null;
  /** Fired whenever a saved disagree note exists (on hydrate or after save). */
  onDisagreeNote?: (note: string) => void;
}) {
  const { user } = useAuth();
  const [agree, setAgree] = useState<boolean | null>(null);
  const [reason, setReason] = useState("");
  const [showReason, setShowReason] = useState(false);
  const [saving, setSaving] = useState(false);

  // Hydrate any existing vote so the user sees their previous state.
  useEffect(() => {
    if (!scanId || !user) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("verdict_feedback")
        .select("agree, reason")
        .eq("scan_id", scanId)
        .eq("user_id", user.id)
        .maybeSingle();
      if (cancelled || !data) return;
      setAgree(data.agree);
      setReason(data.reason ?? "");
      if (data.agree === false && (data.reason ?? "").trim()) {
        onDisagreeNote?.((data.reason ?? "").trim());
      }
    })();
    return () => { cancelled = true; };
  }, [scanId, user, onDisagreeNote]);

  const vote = async (next: boolean) => {
    if (!scanId || !user || saving) return;
    setSaving(true);
    setAgree(next);
    const { error } = await supabase
      .from("verdict_feedback")
      .upsert(
        { scan_id: scanId, user_id: user.id, agree: next, reason: reason || null },
        { onConflict: "scan_id,user_id" },
      );
    setSaving(false);
    if (error) {
      toast.error("Couldn't save feedback");
      return;
    }
    if (!next) setShowReason(true);
    toast.success("Thanks — this helps Fig get sharper.");
  };

  const saveReason = async () => {
    if (!scanId || !user || agree === null) return;
    setSaving(true);
    const { error } = await supabase
      .from("verdict_feedback")
      .upsert(
        { scan_id: scanId, user_id: user.id, agree, reason: reason.trim() || null },
        { onConflict: "scan_id,user_id" },
      );
    setSaving(false);
    if (error) toast.error("Couldn't save");
    else {
      setShowReason(false);
      toast.success("Saved");
      if (agree === false && reason.trim()) onDisagreeNote?.(reason.trim());
    }
  };

  if (!scanId) return null;

  const btn = (active: boolean, tone: "olive" | "rust") => ({
    flex: 1,
    padding: "10px 12px",
    background: active ? `hsl(var(--${tone}))` : "hsl(var(--cream))",
    color: active ? "hsl(var(--cream))" : "hsl(var(--ink))",
    border: "1px solid hsl(var(--ink))",
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: 11,
    letterSpacing: "0.08em",
    textTransform: "uppercase" as const,
    cursor: "pointer",
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{
        fontFamily: "'Fraunces', 'Playfair Display', serif",
        fontSize: 13, color: "hsl(var(--ink-2))", lineHeight: 1.3,
      }}>
        Was this verdict right?
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <button onClick={() => vote(true)} disabled={saving} style={btn(agree === true, "olive")}>
          👍 Spot on
        </button>
        <button onClick={() => vote(false)} disabled={saving} style={btn(agree === false, "rust")}>
          👎 Off
        </button>
      </div>
      {showReason && agree === false && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value.slice(0, 280))}
            placeholder="What did Fig miss? (optional)"
            rows={2}
            style={{
              width: "100%", padding: 8, background: "hsl(var(--cream))",
              border: "1px solid hsl(var(--ink))", fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 12, color: "hsl(var(--ink))", resize: "none",
            }}
          />
          <button onClick={saveReason} disabled={saving} className="btn" style={{ padding: "8px" }}>
            Save note
          </button>
        </div>
      )}
    </div>
  );
}